/**
 * rondo-web-forms
 *
 * Rondo Web Forms
 *
 * @author Jiri Hybek <jiri@hybek.cz>
 * @license See LICENSE file distributed with this source code.
 */

/**
 * Parses number in various decimal formats from string
 *
 * @param value Value
 */
export function parseNumber(value: string, nanValue: number = NaN) {

	const parsed = parseFloat((value || "").replace(",", ".").replace(/ /g, ""));

	if (isNaN(parsed)) {
		return nanValue;
	} else {
		return parsed;
	}

}

/**
 * Formats number to a user friendly format
 * @param value Value
 * @param decimals Decimal count
 * @param decPoint Decimal point seperator
 * @param thousandsSep Thousands separator
 * @param nanValue NaN value placeholder
 */
export function formatNumber(value: number, decimals: number = 0, decPoint: string = ",", thousandsSep: string = " ", nanValue: string = "-") {
	//  discuss at: https://locutus.io/php/number_format/
	// original by: Jonas Raoni Soares Silva (https://www.jsfromhell.com)
	// improved by: Kevin van Zonneveld (https://kvz.io)
	// improved by: davook
	// improved by: Brett Zamir (https://brett-zamir.me)
	// improved by: Brett Zamir (https://brett-zamir.me)
	// improved by: Theriault (https://github.com/Theriault)
	// improved by: Kevin van Zonneveld (https://kvz.io)
	// bugfixed by: Michael White (https://getsprink.com)
	// bugfixed by: Benjamin Lupton
	// bugfixed by: Allan Jensen (https://www.winternet.no)
	// bugfixed by: Howard Yeend
	// bugfixed by: Diogo Resende
	// bugfixed by: Rival
	// bugfixed by: Brett Zamir (https://brett-zamir.me)
	//  revised by: Jonas Raoni Soares Silva (https://www.jsfromhell.com)
	//  revised by: Luke Smith (https://lucassmith.name)
	//    input by: Kheang Hok Chin (https://www.distantia.ca/)
	//    input by: Jay Klehr
	//    input by: Amir Habibi (https://www.residence-mixte.com/)
	//    input by: Amirouche
	//   example 1: number_format(1234.56)
	//   returns 1: '1,235'
	//   example 2: number_format(1234.56, 2, ',', ' ')
	//   returns 2: '1 234,56'
	//   example 3: number_format(1234.5678, 2, '.', '')
	//   returns 3: '1234.57'
	//   example 4: number_format(67, 2, ',', '.')
	//   returns 4: '67,00'
	//   example 5: number_format(1000)
	//   returns 5: '1,000'
	//   example 6: number_format(67.311, 2)
	//   returns 6: '67.31'
	//   example 7: number_format(1000.55, 1)
	//   returns 7: '1,000.6'
	//   example 8: number_format(67000, 5, ',', '.')
	//   returns 8: '67.000,00000'
	//   example 9: number_format(0.9, 0)
	//   returns 9: '1'
	//  example 10: number_format('1.20', 2)
	//  returns 10: '1.20'
	//  example 11: number_format('1.20', 4)
	//  returns 11: '1.2000'
	//  example 12: number_format('1.2000', 3)
	//  returns 12: '1.200'
	//  example 13: number_format('1 000,50', 2, '.', ' ')
	//  returns 13: '100 050.00'
	//  example 14: number_format(1e-8, 8, '.', '')
	//  returns 14: '0.00000001'

	if (isNaN(value)) {
		return nanValue;
	}

	const number = String(value).replace(/[^0-9+\-Ee.]/g, '');
	const n = !isFinite(+number) ? 0 : +number;
	const prec = !isFinite(+decimals) ? 0 : Math.abs(decimals);
	const sep = (typeof thousandsSep === 'undefined') ? ',' : thousandsSep;
	const dec = (typeof decPoint === 'undefined') ? '.' : decPoint;

	const toFixedFix = () => {
		if (("" + n).indexOf("e") === -1) {
			return +(Math.round(parseFloat(n + 'e+' + prec)) + 'e-' + prec)
	  	} else {
			const arr = ('' + n).split('e')
			let sig = ''

			if (+arr[1] + prec > 0) {
			  	sig = '+'
			}

			return (+(Math.round(parseFloat(+arr[0] + 'e' + sig + (+arr[1] + prec))) + 'e-' + prec)).toFixed(prec)
	  	}
	}

	const s = (prec ? toFixedFix().toString() : '' + Math.round(n)).split('.')
	if (s[0].length > 3) {
		s[0] = s[0].replace(/\B(?=(?:\d{3})+(?!\d))/g, sep)
	}
	if ((s[1] || '').length < prec) {
		s[1] = s[1] || ''
		s[1] += new Array(prec - s[1].length + 1).join('0')
	}

	return s.join(dec)
}

/**
 * Number formatter for print values
 *
 * @param decimals Decimal count
 * @param decPoint Decimal point seperator
 * @param thousandsSep Thousands separator
 * @param nanValue NaN value placeholder
 */
export function printNumberFormatter(decimals: number = 0, decPoint: string = ",", thousandsSep: string = " ", nanValue: string = "-") {

	return (value: string) => {

		const parsedValue = parseNumber(value);
		return formatNumber(parsedValue, decimals, decPoint, thousandsSep, nanValue);

	}

}

/**
 * Returns date in a Czech string format
 *
 * @param dt Date
 */
export function getDateString(dt: Date) {

	const f = (val: number) => {
		return val < 10 ? "0" + val : String(val);
	};

	return `${f(dt.getDate())}. ${f(dt.getMonth() + 1)}. ${dt.getFullYear()}`;

}